/** Small procedural instrument: felt keys, wooden taps, glass chimes and water. */
export class GardenAudio {
  private context: AudioContext | null = null;
  private master: GainNode | null = null;
  private musicBus: GainNode | null = null;
  private effectsBus: GainNode | null = null;
  private readonly sources = new Set<AudioScheduledSourceNode>();
  private enabled = false;
  private music = true;
  private volume = 0.55;
  private playing = false;
  private timer = 0;
  private beat = 0;
  private nextNote = 0;
  private lastMove = -1;

  /** Call synchronously from a user gesture, before loading the game bundle. */
  unlock(): void {
    if (!this.enabled || !window.AudioContext) return;
    try {
      if (!this.context) {
        this.context = new AudioContext();
        this.master = this.context.createGain();
        this.musicBus = this.context.createGain();
        this.effectsBus = this.context.createGain();
        const limiter = this.context.createDynamicsCompressor();
        limiter.threshold.value = -14;
        limiter.ratio.value = 8;
        this.musicBus.connect(this.master);
        this.effectsBus.connect(this.master);
        this.master.connect(limiter).connect(this.context.destination);
        this.master.gain.value = 0;
      }
      void this.context
        .resume()
        .then(() => this.sync())
        .catch(() => {});
    } catch {
      // Audio is optional, including in browsers that disallow an audio context.
    }
  }
  configure(enabled: boolean, music: boolean, volume: number): void {
    this.enabled = enabled;
    this.music = music;
    this.volume = Math.max(0, Math.min(1, volume));
    this.sync();
  }
  setPlaying(playing: boolean): void {
    if (this.playing === playing) return;
    this.playing = playing;
    this.sync();
  }
  private sync(): void {
    if (!this.context || !this.master || !this.musicBus || !this.effectsBus) return;
    const now = this.context.currentTime;
    const audible = this.enabled && this.playing;
    this.master.gain.cancelScheduledValues(now);
    this.master.gain.setTargetAtTime(audible ? this.volume : 0, now, 0.025);
    this.musicBus.gain.setTargetAtTime(this.music ? 0.48 : 0, now, 0.08);
    this.effectsBus.gain.value = 0.8;
    if (!audible || !this.music) {
      window.clearInterval(this.timer);
      this.timer = 0;
      if (!audible) {
        for (const source of this.sources) {
          try {
            source.stop(now + 0.12);
          } catch {
            /* Already stopped. */
          }
        }
        this.lastMove = -1;
      }
    } else if (!this.timer) {
      this.nextNote = now + 0.08;
      this.schedule();
      this.timer = window.setInterval(() => this.schedule(), 180);
    }
  }
  private schedule(): void {
    if (!this.context || this.context.state !== 'running') return;
    const melody = [0, 7, 12, 16, 14, 7, 4, 12, 0, 9, 12, 19, 16, 12, 7, 4];
    while (this.nextNote < this.context.currentTime + 0.3) {
      const degree = melody[this.beat % melody.length]!;
      this.tone(220 * 2 ** (degree / 12), this.nextNote, 1.65, 0.095, this.musicBus!, 'sine');
      if (this.beat % 4 === 0) {
        const root = this.beat % 16 < 8 ? 110 : 146.83;
        for (const ratio of [1, 1.5, 2])
          this.tone(root * ratio, this.nextNote, 3.5, 0.028, this.musicBus!, 'sine', 0.3);
      }
      this.nextNote += 0.6;
      this.beat++;
    }
  }
  private tone(
    frequency: number,
    start: number,
    duration: number,
    level: number,
    bus: GainNode,
    type: OscillatorType = 'sine',
    attack = 0.012,
    endFrequency?: number,
  ): void {
    const ctx = this.context!;
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    oscillator.type = type;
    oscillator.frequency.setValueAtTime(frequency, start);
    if (endFrequency)
      oscillator.frequency.exponentialRampToValueAtTime(endFrequency, start + duration);
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(level, start + attack);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + duration);
    oscillator.connect(gain).connect(bus);
    oscillator.start(start);
    oscillator.stop(start + duration + 0.03);
    this.sources.add(oscillator);
    oscillator.onended = () => {
      oscillator.disconnect();
      gain.disconnect();
      this.sources.delete(oscillator);
    };
  }
  play(
    kind: 'move' | 'rotate' | 'land' | 'bloom' | 'water' | 'season' | 'finish',
    intensity = 1,
  ): void {
    if (!this.enabled || !this.playing || !this.context || this.context.state !== 'running') return;
    const now = this.context.currentTime;
    if (kind === 'move' && now - this.lastMove < 0.065) return;
    if (kind === 'move') this.lastMove = now;
    const bus = this.effectsBus!;
    if (kind === 'move' || kind === 'land') {
      this.tone(
        kind === 'land' ? 165 : 330,
        now,
        kind === 'land' ? 0.22 : 0.055,
        kind === 'land' ? 0.16 : 0.035,
        bus,
        'sine',
        0.004,
        kind === 'land' ? 58 : 190,
      );
      if (kind === 'land') this.tone(880, now, 0.09, 0.035, bus, 'triangle');
      return;
    }
    const notes =
      kind === 'rotate'
        ? [440, 659.25]
        : kind === 'water'
          ? [1174.66, 880, 659.25, 587.33, 440]
          : kind === 'finish'
            ? [659.25, 587.33, 440, 329.63]
            : kind === 'season'
              ? [392, 493.88, 587.33, 783.99, 987.77]
              : intensity >= 4
                ? [523.25, 659.25, 783.99, 1046.5, 1318.51, 1567.98]
                : [523.25, 659.25, 783.99, 1046.5];
    notes.forEach((frequency, index) => {
      const start = now + index * (kind === 'rotate' ? 0.035 : 0.085);
      const duration = kind === 'rotate' ? 0.2 : 0.85;
      this.tone(frequency, start, duration, 0.095, bus);
      this.tone(frequency * 2.003, start, duration * 0.4, 0.02, bus);
      if (kind === 'water')
        this.tone(frequency / 2, start, 0.14, 0.08, bus, 'sine', 0.005, frequency * 0.9);
    });
  }
  destroy(): void {
    window.clearInterval(this.timer);
    this.timer = 0;
    if (this.context) void this.context.close().catch(() => {});
    this.context = null;
    this.sources.clear();
  }
}

export const gardenAudio = new GardenAudio();
